import React from 'react';
import { Box, Typography, Paper, Divider, Link } from '@mui/material';

export default function PrivacyPolicy() {
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: January 31, 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          1. Introduction
        </Typography>
        <Typography variant="body1" paragraph>
          Welcome to the Interclub Competition Manager ("we," "our," or "us"). We are committed to protecting 
          your personal information and your right to privacy. This Privacy Policy explains how we collect, 
          use, disclose, and safeguard your information when you use our website and services.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          2. Information We Collect
        </Typography>
        <Typography variant="body1" paragraph>
          We collect information that you provide directly to us, including:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body1">
              <strong>Account Information:</strong> Name, email address, phone number, and password when you create an account.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Skater Information:</strong> Skater names, dates of birth, U.S. Figure Skating membership IDs, 
              club affiliations, skating levels, and coach information.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Competition Data:</strong> Competition results, scores, placements, and competition history.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Payment Information:</strong> When you pay registration fees, payment processing is handled 
              by Stripe. We do not store your full credit card information on our servers.
            </Typography>
          </li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          3. How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use the information we collect to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Provide, maintain, and improve our services</Typography></li>
          <li><Typography variant="body1">Process season registrations and payments</Typography></li>
          <li><Typography variant="body1">Track and display competition results and standings</Typography></li>
          <li><Typography variant="body1">Communicate with you about your account and competitions</Typography></li>
          <li><Typography variant="body1">Verify U.S. Figure Skating membership status</Typography></li>
          <li><Typography variant="body1">Comply with legal obligations</Typography></li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          4. Information Sharing
        </Typography>
        <Typography variant="body1" paragraph>
          We may share your information in the following circumstances:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body1">
              <strong>With Interclub Administrators:</strong> Club officials and interclub administrators 
              can view skater information for competition management purposes.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Competition Results:</strong> Competition results and standings are displayed publicly 
              to participants and may be shared with U.S. Figure Skating.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Service Providers:</strong> We work with third-party service providers (such as Stripe 
              for payments and Firebase for hosting) who need access to your information to provide services.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Legal Requirements:</strong> We may disclose information if required by law or in 
              response to valid legal processes.
            </Typography>
          </li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          5. Data Security
        </Typography>
        <Typography variant="body1" paragraph>
          We implement appropriate technical and organizational security measures to protect your personal 
          information. However, no method of transmission over the Internet or electronic storage is 100% 
          secure, and we cannot guarantee absolute security.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          6. Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          We retain your personal information for as long as your account is active or as needed to provide 
          you services. Competition history and results may be retained indefinitely for record-keeping 
          purposes. You may request deletion of your account by contacting us.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          7. Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          Our service involves registering minor skaters. We collect information about minor skaters only 
          with the consent of their parent or guardian who creates and manages the account. Parents and 
          guardians can review, update, or request deletion of their children's information at any time.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          8. Your Rights
        </Typography>
        <Typography variant="body1" paragraph>
          You have the right to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Access the personal information we hold about you</Typography></li>
          <li><Typography variant="body1">Request correction of inaccurate information</Typography></li>
          <li><Typography variant="body1">Request deletion of your account and associated data</Typography></li>
          <li><Typography variant="body1">Opt out of non-essential communications</Typography></li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          9. Third-Party Services
        </Typography>
        <Typography variant="body1" paragraph>
          Our service integrates with:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body1">
              <strong>Google Firebase:</strong> For authentication and data storage. 
              See <Link href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener">Firebase Privacy Policy</Link>.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>Stripe:</strong> For payment processing. 
              See <Link href="https://stripe.com/privacy" target="_blank" rel="noopener">Stripe Privacy Policy</Link>.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <strong>U.S. Figure Skating:</strong> For membership verification and competition data.
            </Typography>
          </li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          10. Changes to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
          the new Privacy Policy on this page and updating the "Last updated" date.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          11. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions about this Privacy Policy or our data practices, please contact your 
          interclub administrator or reach out through the contact information provided by your skating club.
        </Typography>
      </Paper>
    </Box>
  );
}
